import { z } from 'zod'
import { UserPermission } from './permissions'
import { RoleName, ROLES } from './roles'

export const isRole = (value: string): value is RoleName => value in ROLES

export const zRole = z.string().refine(isRole, { message: 'Invalid role.' })

export const hasPermissions = (granted: UserPermission[], ...required: UserPermission[]) => {
  const grantedSet = new Set(granted)
  const requiredSet = new Set(required)

  return requiredSet.difference(grantedSet).size === 0
}

export const hasOneOfPermissions = (granted: UserPermission[], ...required: UserPermission[]) => {
  const grantedSet = new Set(granted)
  const requiredSet = new Set(required)
  return requiredSet.intersection(grantedSet).size > 0
}

// Substrings that mark a permission as a read (non-mutating) action. `view` covers the vast majority;
// `export` covers CSV/download permissions, which only read data. Every create/edit/delete/publish/
// send/upload/user-management/operations/sync permission omits both, so new mutating permissions
// still fail safe (treated as mutating).
const READ_ONLY_PERMISSION_MARKERS = ['view', 'export'] as const

/**
 * A user is read-only when they have at least one permission and every effective
 * permission is a read permission (see READ_ONLY_PERMISSION_MARKERS).
 *
 * Callers should pass the user's fully resolved effective permissions (roles
 * unioned with per-user overrides), e.g. the result of getUserPermissions.
 */
export const isReadOnlyPermissionSet = (permissions: readonly string[]): boolean =>
  permissions.length > 0 &&
  permissions.every((permission) =>
    READ_ONLY_PERMISSION_MARKERS.some((marker) => permission.includes(marker)),
  )

export type VerifiedPermissions = ReturnType<typeof hasPermissions>
